import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  EVENT_TYPES,
  EVENT_LABELS,
  buildDailyChartData,
  buildPluginRows,
} from "@/lib/scrapingEvents";
import { ScrapingDailyAreaChart } from "@/components/charts/ScrapingDailyAreaChart";
import { WorkerCell } from "@/components/worker/WorkerCell";
import {
  getScrapingDailyStats,
  getScrapingByPlugin,
  getScrapingTotals,
  DailyStatRow,
  ByPluginRow,
  WorkerInfo,
} from "@/services/scrapingStatsApi";

export default function ScrapingStatsPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [dailyData, setDailyData] = useState<DailyStatRow[]>([]);
  const [pluginData, setPluginData] = useState<ByPluginRow[]>([]);
  const [workersMap, setWorkersMap] = useState<Record<string, WorkerInfo>>({});
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [daily, byPlugin, totalsRes] = await Promise.all([
          getScrapingDailyStats(navigate, { days }),
          getScrapingByPlugin(navigate, { days }),
          getScrapingTotals(navigate, { days }),
        ]);
        if (cancelled) return; // évite d'écraser un résultat plus récent
        setDailyData(daily.data);
        setPluginData(byPlugin.data);
        setWorkersMap(byPlugin.workers ?? {});
        setTotals(totalsRes.totals);
      } catch (e) {
        if (!cancelled) setError((e as Error)?.message || "Erreur");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days, navigate]);

  const chartData = useMemo(() => buildDailyChartData(dailyData), [dailyData]);
  const pluginRows = useMemo(() => buildPluginRows(pluginData), [pluginData]);

  const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);

  return (
    <>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Scraping Stats</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          {/* Period selector + KPI totals */}
          <div className="flex items-center gap-4">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 derniers jours</SelectItem>
                <SelectItem value="30">30 derniers jours</SelectItem>
                <SelectItem value="90">90 derniers jours</SelectItem>
                <SelectItem value="365">12 derniers mois</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{grandTotal.toLocaleString("fr-FR")}</div>
              </CardContent>
            </Card>
            {EVENT_TYPES.map((type) => (
              <Card key={type}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">{EVENT_LABELS[type]}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {(totals[type] || 0).toLocaleString("fr-FR")}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Daily chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Volume de scraping par jour</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrapingDailyAreaChart chartData={chartData} loading={loading} />
            </CardContent>
          </Card>

          {/* Per-plugin table */}
          <TooltipProvider delayDuration={150}>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Volume par plugin</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">Chargement...</div>
                ) : pluginRows.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">Aucune donnee</div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Worker</TableHead>
                          {EVENT_TYPES.map((type) => (
                            <TableHead key={type} className="text-right">{EVENT_LABELS[type]}</TableHead>
                          ))}
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pluginRows.map((row) => (
                          <TableRow key={row.worker_id}>
                            <TableCell>
                              <WorkerCell workerId={row.worker_id} info={workersMap[row.worker_id]} />
                            </TableCell>
                            {EVENT_TYPES.map((type) => (
                              <TableCell key={type} className="text-right">{(row[type] || 0).toLocaleString("fr-FR")}</TableCell>
                            ))}
                            <TableCell className="text-right font-semibold">{row.total.toLocaleString("fr-FR")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TooltipProvider>
        </div>
      </SidebarInset>
    </>
  );
}
